import React, { useState, useEffect, useRef } from 'react';
import { Image, Film, Music, ChevronDown } from 'lucide-react';
import FontPicker from '../UI/FontPicker';
import CaptionStylePopup from '../Canvas/PropertiesPanel/CaptionStylePopup';
import api from '../../services/api';
import { DEFAULT_CAPTION_STYLE } from '../../utils/textStylePresets';

/**
 * ExpandedFields — renders all fields for a layer grouped by category
 * (text, image, video, audio, color) in the "Show all properties" view.
 *
 * Props:
 *   openFontFieldIndex  – the fieldIndex of the currently open font picker (null = none)
 *   onOpenFontField     – callback(fieldIndex | null) to request opening/closing a font picker
 */
const ExpandedFields = ({ layer, onFieldChange, onMediaClick, onPropertyChange, openFontFieldIndex, onOpenFontField }) => {
  if (!layer || layer.fields.length === 0) return null;

  const grouped = {};
  layer.fields.forEach((f) => {
    if (!grouped[f.category]) grouped[f.category] = [];
    grouped[f.category].push(f);
  });

  return (
    <div className="space-y-3">
      {/* Text fields — label once, then compact boxes flow inline */}
      {grouped.text && grouped.text.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-xs text-gray-400 mr-1 mt-1.5">Text</span>
          {grouped.text.map((field) => (
            <ExpandedTextField
              key={`t-${field.fieldIndex}`}
              layer={layer}
              field={field}
              onFieldChange={onFieldChange}
              onPropertyChange={onPropertyChange}
              isFontOpen={openFontFieldIndex === field.fieldIndex}
              onRequestFontOpen={(open) => onOpenFontField?.(open ? field.fieldIndex : null)}
            />
          ))}
        </div>
      )}

      {/* Image fields */}
      {grouped.image && grouped.image.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Image</span>
          {grouped.image.map((field) => (
            <MediaThumbBtn
              key={`i-${field.fieldIndex ?? 'el'}`}
              field={field}
              icon={<Image size={16} />}
              onClick={() => onMediaClick(layer, field)}
              title="Click to change image"
            />
          ))}
        </div>
      )}

      {/* Video fields */}
      {grouped.video && grouped.video.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Video</span>
          {grouped.video.map((field) => (
            <MediaThumbBtn
              key={`v-${field.fieldIndex ?? 'el'}`}
              field={field}
              icon={<Film size={16} />}
              isVideo
              onClick={() => onMediaClick(layer, field)}
              title="Click to change video"
            />
          ))}
        </div>
      )}

      {/* Audio fields */}
      {grouped.audio && grouped.audio.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Audio</span>
          {grouped.audio.map((field) => (
            <button
              key={`a-${field.fieldIndex ?? 'el'}`}
              className="w-14 h-14 rounded-lg overflow-hidden border border-ag-black-400 hover:border-ag-green transition-all cursor-pointer hover:scale-105 bg-ag-black-600 flex-shrink-0"
              onClick={() => onMediaClick(layer, field)}
              title="Click to change audio"
            >
              <div className="flex items-center justify-center h-full bg-gradient-to-br from-purple-900/60 to-blue-900/60">
                <Music size={16} className="text-purple-300" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Color fields */}
      {grouped.color && grouped.color.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Color</span>
          {grouped.color.map((field) => (
            <label key={`c-${field.fieldIndex}`} className="cursor-pointer">
              <input
                type="color"
                value={field.value || '#000000'}
                onChange={(e) => onFieldChange(layer, field, e.target.value)}
                className="sr-only"
              />
              <div
                className="w-8 h-8 rounded border-2 border-ag-black-400 hover:border-ag-green transition-all"
                style={{ backgroundColor: field.value || '#000000' }}
                title={field.value || '#000000'}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * MediaThumbBtn — small clickable thumbnail for image/video fields.
 */
const MediaThumbBtn = ({ field, icon, isVideo, onClick, title }) => (
  <button
    className="w-14 h-14 rounded-lg overflow-hidden border border-ag-black-400 hover:border-ag-green transition-all cursor-pointer hover:scale-105 bg-ag-black-600 flex-shrink-0"
    onClick={onClick}
    title={title}
  >
    {field.value ? (
      isVideo ? (
        <video crossOrigin='anonymous' src={field.value} poster={field.poster || undefined} className="w-full h-full object-cover" muted playsInline preload="auto" onLoadedData={(e) => { if (!field.poster) { e.target.currentTime = 0.1; } }} />
      ) : (
        <img
          crossOrigin='anonymous'
          src={field.value}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )
    ) : (
      <div className="flex items-center justify-center h-full text-gray-500">{icon}</div>
    )}
  </button>
);

/**
 * ExpandedTextField — compact inline text box with integrated font dropdown arrow.
 * Flows inline within a flex-wrap container. Clicking the text edits it;
 * clicking the chevron opens the font picker upward.
 
 */
const ExpandedTextField = ({ layer, field, onFieldChange, onPropertyChange, isFontOpen, onRequestFontOpen }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(field.value || '');
  const textareaRef = useRef(null);
  const toggleBtnRef = useRef(null);

  // Expose the popup control
  const [isStylePopupOpen, setIsStylePopupOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(field.value || '');
  }, [field.value, isEditing]);

  // Adjust textarea height automatically based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [draft, isEditing]);

  const confirm = () => {
    onFieldChange(layer, field, draft);
    setIsEditing(false);
  };

  const cancel = () => {
    setDraft(field.value || '');
    setIsEditing(false);
  };

  const displayValue = field.value
    ? field.value.length > 25
      ? field.value.slice(0, 25) + '\u2026'
      : field.value
    : 'Empty text';

  const hasFontPicker = field.kind === 'field' && field.fieldType && !isEditing;
  const isTextElement = field.kind === 'element' && field.originalType === 'text';
  const isCaptionElement = field.kind === 'element' && field.originalType === 'caption';

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleTogglePopup = (e) => {
    e.stopPropagation();
    setIsStylePopupOpen((prev) => !prev);
  };

  const generateCaptions = async () => {
    // Need to find the media URL from the layer's fields
    const mediaField = layer.fields.find(f => f.category === 'video' || f.category === 'audio' || f.originalType === 'video' || f.originalType === 'audio');
    const mediaUrl = mediaField ? mediaField.value : layer.element?.url || layer.element?.src || layer.element?.componentData?.url;
    
    if (!mediaUrl || mediaUrl.startsWith('blob:')) {
      alert('Cannot generate captions for locally uploaded media or no media found.');
      return;
    }

    setIsGenerating(true);
    try {
      // 1. Call transcription API
      const response = await api.post('/media/transcribe', { url: mediaUrl });
      const transcript = response.data.data.transcript;

      // 2. Iterate over words/sentences and generate subtitles
      if (transcript.words && transcript.words.length > 0) {
        const GAP_THRESHOLD_MS = 1500; // Split phrases when gap between words exceeds this (e.g. music breaks)
        const phrases = [];
        let currentPhrase = [];
        let currentPhraseDuration = 0;

        const flushPhrase = () => {
          if (currentPhrase.length > 0) {
            phrases.push({
              text: currentPhrase.map(w => w.text).join(' '),
              start: currentPhrase[0].start,
              end: currentPhrase[currentPhrase.length - 1].end,
              words: currentPhrase.map(w => ({ text: w.text, start: w.start, end: w.end }))
            });
            currentPhrase = [];
            currentPhraseDuration = 0;
          }
        };

        transcript.words.forEach(word => {
          // If there is a large time gap between the previous word and this one
          // (e.g. a music break), flush the current phrase before starting a new one.
          if (
            currentPhrase.length > 0 &&
            word.start - currentPhrase[currentPhrase.length - 1].end > GAP_THRESHOLD_MS
          ) {
            flushPhrase();
          }

          currentPhrase.push(word);
          currentPhraseDuration += (word.end - word.start);

          if (currentPhrase.length >= 3 || word.text.match(/[.!?]$/)) {
            flushPhrase();
          }
        });

        flushPhrase();

        const captionData = {
          phrases,
          style: { ...DEFAULT_CAPTION_STYLE, ...(layer.element?.componentData?.captions?.style || {}) }
        };

        onPropertyChange('componentData.captions', captionData, false, layer.element);

        // PIXI.Text sprite creation for canvas updating is handled by CanvasStore when fields change,
        // but similarly to AgComponentProperties, we can manually create it if needed. For now, rely on standard store sync.
      }
    } catch (error) {
      console.error('Error generating captions:', error);
      alert('Failed to generate captions. Please check console for details.');
    } finally {
      setIsGenerating(false);
    }
  };

  const isPlayableMedia = layer.fields?.some(f => f.category === 'video' || f.category === 'audio' || f.originalType === 'video' || f.originalType === 'audio');
  const mediaUrl = isPlayableMedia ? (layer.fields.find(f => f.category === 'video' || f.category === 'audio' || f.originalType === 'video' || f.originalType === 'audio')?.value || layer.element?.url || layer.element?.src || layer.element?.componentData?.url) : null;
  const isLocalUpload = typeof mediaUrl === 'string' && mediaUrl.startsWith('blob:');

  if (isEditing) {
    return (
      <div className="flex flex-col gap-1.5 w-full">
        <textarea
          ref={textareaRef}
          className="w-full bg-ag-black-900 text-white text-sm rounded-lg border border-ag-black-400 focus:border-ag-green focus:outline-none p-2 resize-none overflow-hidden"
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirm(); }
            else if (e.key === 'Escape') cancel();
          }}
          autoFocus
        />
        <div className="flex gap-1.5 justify-end">
          <button className="px-3 py-1 text-xs rounded bg-ag-black-600 text-gray-300 hover:text-white transition-colors" onClick={cancel}>Cancel</button>
          <button className="px-3 py-1 text-xs rounded bg-ag-green text-black font-medium hover:brightness-110 transition-all" onClick={confirm}>Save</button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full mb-1 flex flex-col items-end">
      <div className="flex items-stretch bg-ag-black-800 rounded border border-ag-black-400 hover:border-ag-green transition-colors min-h-[36px] w-full">
        {/* Clickable text value fills available space */}
        <button
          className="flex-1 text-left text-sm text-white px-3 py-2 cursor-pointer break-all"
          onClick={handleStartEditing}
          title={field.value || 'Click to edit'}
        >
          {displayValue}
        </button>

        {/* Style/Properties Pop-up Toggle Button */}
        {(hasFontPicker || isTextElement || isCaptionElement) && (
          <button
            ref={toggleBtnRef}
            className="flex items-center justify-center px-3 border-l border-ag-black-400 text-gray-400 hover:text-white hover:bg-ag-black-700 transition-colors cursor-pointer rounded-r"
            onClick={handleTogglePopup}
            title="Edit Style"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>
        )}
      </div>

      {isStylePopupOpen && (
        <CaptionStylePopup
          currentStyle={{
            ...(isCaptionElement
              ? { ...DEFAULT_CAPTION_STYLE, ...(layer?.element?.componentData?.captions?.style || {}) }
              : {
                fontFamily: field.fontFamily || layer?.element?.componentData?.fontFamily || 'Arial',
                fontSize: layer?.element?.componentData?.fontSize || 48,
                color: layer?.element?.componentData?.color || '#ffffff'
              })
          }}
          direction="down"
          anchorRef={toggleBtnRef}
          mode={isCaptionElement ? 'caption' : 'text'}
          onChange={(styleUpdate) => {
            if (!layer?.element) return;
            if (isTextElement) {
              if (styleUpdate.fontFamily) onPropertyChange('componentData.fontFamily', styleUpdate.fontFamily, false, layer.element);
              if (styleUpdate.fontSize) onPropertyChange('componentData.fontSize', styleUpdate.fontSize, false, layer.element);
              if (styleUpdate.color) onPropertyChange('componentData.color', styleUpdate.color, false, layer.element);
            } else if (isCaptionElement) {
              const currentCaptions = layer.element.componentData?.captions;
              if (currentCaptions) {
                 const newStyle = { ...currentCaptions.style, ...styleUpdate };
                 onPropertyChange('componentData.captions', { ...currentCaptions, style: newStyle }, false, layer.element);
              }
            } else if (hasFontPicker) {
              if (styleUpdate.fontFamily) {
                onPropertyChange(
                  `componentData.fields[${field.fieldIndex}].${field.fieldType}_fontFamily`,
                  styleUpdate.fontFamily,
                  false,
                  layer.element
                );
              }
            }
          }}
          onClose={() => setIsStylePopupOpen(false)}
        />
      )}
      
      {/* Generate/Regenerate Button for Captions */}
      {isCaptionElement && !isLocalUpload && (
        <button
          onClick={generateCaptions}
          disabled={isGenerating}
          className={`mt-2 px-3 py-1 text-[10px] font-medium rounded-full transition-colors flex items-center justify-center gap-1.5 w-max
            ${isGenerating ? 'bg-ag-black-600 text-gray-400 cursor-not-allowed' : 'bg-[#d4f391] hover:bg-[#c3e88d] text-black'}`}
        >
          {isGenerating ? (
            <>
              <div className="w-3 h-3 border-2 border-gray-400 border-t-black rounded-full animate-spin"></div>
              Generating...
            </>
          ) : draft && draft !== 'Empty text' && draft !== 'Generate Captions' ? (
            'Re-generate'
          ) : (
            'Generate'
          )}
        </button>
      )}
    </div>
  );
};

export default ExpandedFields;
